import type {
	CoreApiPaginatedResponse,
	DetailQueryOptions,
	IUserSettingsBrandOption,
	IUserSettingsClientOption,
	IUserSettingsHotelOption,
	IUserSettingsRegionOption,
	PaginatedParams,
	PaginatedQueryOptions,
	UserCollection,
	UserDepartmentRef,
	UserItem,
	UserPropertyLevel,
	UserRole,
	UserStatus,
} from '@lqa-frontend/api-services';
import {
	endpoints,
	useDetailQuery,
	usePaginatedQuery,
} from '@lqa-frontend/api-services';
import { keepPreviousData } from '@tanstack/react-query';

export function useUsersQuery(
	params: UsersQueryParams,
	options?: PaginatedQueryOptions<UserCollection>
) {
	return usePaginatedQuery<UserCollection>(
		endpoints.userSettings.users.index,
		params,
		{ placeholderData: keepPreviousData, ...options }
	);
}

export interface UsersQueryParams extends PaginatedParams {
	search?: string;
	statuses?: UserStatus[];
	role?: UserRole;
	hasUserManagementAccess?: boolean;
	propertyLevel?: UserPropertyLevel;
	clientId?: number;
	brandId?: number;
	hotelId?: number;
	webAccess?: boolean;
	appAccess?: boolean;
	hasPersonalEmail?: boolean;
	hasPhoneNumber?: boolean;
}

export function useUserQuery(
	id?: number,
	options?: Omit<DetailQueryOptions<UserItem>, 'queryKey' | 'queryFn'>
) {
	return useDetailQuery<UserItem>(
		endpoints.userSettings.users.item,
		id,
		options
	);
}

export function useUserDepartmentsQuery(
	params: UserDepartmentsQueryParams,
	options?: PaginatedQueryOptions<UserDepartmentRef>
) {
	return usePaginatedQuery<UserDepartmentRef>(
		endpoints.userSettings.departments.index,
		params,
		options
	);
}

export function useUserSettingsClientsQuery(
	params: IUserSettingsClientsQueryParams,
	options?: PaginatedQueryOptions<IUserSettingsClientOption>
) {
	return usePaginatedQuery<
		IUserSettingsClientOption,
		CoreApiPaginatedResponse<IUserSettingsClientOption>,
		IUserSettingsClientsQueryParams
	>(endpoints.userSettings.clients.index, params, options);
}

export function useUserSettingsBrandsQuery(
	params: IUserSettingsBrandsQueryParams,
	options?: PaginatedQueryOptions<IUserSettingsBrandOption>
) {
	return usePaginatedQuery<
		IUserSettingsBrandOption,
		CoreApiPaginatedResponse<IUserSettingsBrandOption>,
		IUserSettingsBrandsQueryParams
	>(endpoints.userSettings.brands.index, params, options);
}

export function useUserSettingsRegionsQuery(
	params: IUserSettingsPropertiesQueryParams,
	options?: PaginatedQueryOptions<IUserSettingsRegionOption>
) {
	return usePaginatedQuery<
		IUserSettingsRegionOption,
		CoreApiPaginatedResponse<IUserSettingsRegionOption>,
		IUserSettingsPropertiesQueryParams
	>(endpoints.userSettings.regions.index, params, options);
}

export function useUserSettingsHotelsQuery(
	params: IUserSettingsPropertiesQueryParams,
	options?: PaginatedQueryOptions<IUserSettingsHotelOption>
) {
	return usePaginatedQuery<
		IUserSettingsHotelOption,
		CoreApiPaginatedResponse<IUserSettingsHotelOption>,
		IUserSettingsPropertiesQueryParams
	>(endpoints.userSettings.hotels.index, params, options);
}

export interface IUserSettingsClientsQueryParams extends Omit<
	PaginatedParams,
	'sort'
> {
	search?: string;
}

export interface IUserSettingsBrandsQueryParams extends IUserSettingsClientsQueryParams {
	clientId?: number;
}

export interface IUserSettingsPropertiesQueryParams extends IUserSettingsBrandsQueryParams {
	brandIds?: number[];
}

export interface UserDepartmentsQueryParams extends PaginatedParams {
	level: UserPropertyLevel.department | UserPropertyLevel.subDepartment;
	hotelIds: number[];
	search?: string;
}
